import React from 'react';
import { Dropdown } from '@doveit/bricks';
import useContactOptIns from '../../hooks/use-contact-opt-ins/useContactOptIns';
import { createOptIn, deleteOptIn } from '../../../providers/api/opt-in/optInProvider';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import BaseOptInsWidget from '../../../opt-ins/containers/base-optins-widget/BaseOptInsWidget';
import BaseUpdateOptInsAction, { GenericOptIn } from '../../../opt-ins/containers/base-update-optins-action/BaseUpdateOptInsAction';
import { OptIn } from '../../../providers/api/dtos';

export interface ContactOptInsWidgetProps {
  contactId: string;
  canAdd?: boolean;
  canDelete?: boolean;
  canSeeDetails?: boolean;
  onLoadError?: VoidFunction;
}

const ContactOptInsWidget: React.FC<ContactOptInsWidgetProps> = ({
  contactId,
  canAdd = false,
  canDelete = false,
  canSeeDetails = false,
  onLoadError,
}) => {
  const isMobile = useIsDevice('mobile');

  const {
    data: optIns,
    error: optInsError,
    isLoading: areOptInsLoading,
    mutate: mutateOptIns,
  } = useContactOptIns(contactId, {
    onError: onLoadError,
  });

  const handleAdd = React.useCallback(async (optIn: GenericOptIn) => {
    await createOptIn({ ...optIn, contactId });
    await mutateOptIns();
  }, [contactId, mutateOptIns]);

  const handleDelete = React.useCallback(async (optIn: GenericOptIn) => {
    const castedOptIn = optIn as OptIn;
    if (!castedOptIn.id) return;
    await deleteOptIn(castedOptIn.id);
    await mutateOptIns();
  }, [mutateOptIns]);

  const actionComponent = optIns && canSeeDetails ? (
    <BaseUpdateOptInsAction
      optIns={optIns}
      onAdd={handleAdd}
      onDelete={handleDelete}
      canAdd={canAdd}
      canDelete={canDelete}
    />
  ) : undefined;

  const mobileActionComponent = optIns && canSeeDetails ? (
    <BaseUpdateOptInsAction
      optIns={optIns}
      onAdd={handleAdd}
      onDelete={handleDelete}
      canAdd={canAdd}
      canDelete={canDelete}
    >
      {({ update, label, isSaving }) => (
        <Dropdown.Option
          label={label}
          aria-label="Apri modale di modifica consensi"
          onClick={update}
          loading={isSaving}
        />
      )}
    </BaseUpdateOptInsAction>
  ) : undefined;

  return (
    <BaseOptInsWidget
      optIns={optIns}
      isLoading={areOptInsLoading}
      error={!!optInsError}
      primaryAction={!isMobile ? actionComponent : undefined}
      secondaryAction={isMobile ? mobileActionComponent : undefined}
    />
  );
};

export default ContactOptInsWidget;
